import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, Printer, Send, TriangleAlert } from 'lucide-react';
import { formatMoney, formatRate, type PrintJobKind, type Receipt } from '@sebastian/shared';
import { PAYMENT_METHOD_LABELS } from '@sebastian/shared';
import { api, ApiError } from '../lib/api';
import { POLL, keys } from '../hooks/queries';
import { useToast } from '../hooks/useToast';
import { formatClock, formatDate } from '../lib/format';
import { Button } from './ui';

/**
 * The printable receipt. Deliberately styled as paper — cream stock, monospaced
 * figures, 80mm column — and every number comes from the order's own snapshot.
 */
export function ReceiptSheet({ receipt, kind = 'RECEIPT' }: { receipt: Receipt; kind?: PrintJobKind }) {
  const { settings, totals, order } = receipt;
  const money = (cents: number) => formatMoney(cents, settings.currencySymbol, settings.currencyDecimals);

  const Row = ({ label, value, strong }: { label: string; value: string; strong?: boolean }) => (
    <div className={`flex justify-between gap-4 ${strong ? 'text-base font-bold' : 'text-[13px]'}`}>
      <span>{label}</span>
      <span className="tnum">{value}</span>
    </div>
  );

  return (
    <div className="print-sheet mx-auto w-full max-w-[420px] bg-[#faf7f0] px-8 py-8 font-mono text-[13px] leading-relaxed text-[#1a1713] shadow-2xl">
      <div className="text-center">
        {settings.logoUrl ? (
          <img
            src={settings.logoUrl}
            alt=""
            className="mx-auto mb-3 h-auto max-h-20 w-auto max-w-[60%] object-contain"
          />
        ) : (
          <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full border-2 border-[#8f7237] text-3xl font-bold">
            S
          </div>
        )}
        <div className="font-sans text-2xl font-bold uppercase tracking-[0.3em]">
          {settings.restaurantName}
        </div>
        <div className="mt-3 space-y-0.5 text-[11px] leading-snug">
          {settings.addressLine1 ? <div>{settings.addressLine1}</div> : null}
          {settings.addressLine2 ? <div>{settings.addressLine2}</div> : null}
          {settings.phone ? <div>{settings.phone}</div> : null}
          {settings.email ? <div>{settings.email}</div> : null}
          {settings.website ? <div>{settings.website}</div> : null}
          {settings.taxNumber ? <div className="mt-1">Tax No. {settings.taxNumber}</div> : null}
        </div>
      </div>

      <div className="my-4 border-t-2 border-dashed border-[#c3b79d]" />

      {/* A pre-payment bill is not a fiscal document; say so on the paper so a
          guest never mistakes one for a receipt. */}
      {kind === 'BILL' ? (
        <div className="mb-3 text-center text-[11px] font-bold uppercase tracking-[0.35em]">
          — Bill —
        </div>
      ) : null}

      <div className="space-y-0.5 text-[12px]">
        <Row label={kind === 'BILL' ? 'Bill' : 'Receipt'} value={`#${receipt.receiptNumber}`} />
        <Row label="Order" value={`#${order.orderNumber}`} />
        <Row label="Table" value={String(order.tableNumber).padStart(2, '0')} />
        <Row label="Guests" value={String(order.guestCount)} />
        {receipt.staffName ? <Row label="Served by" value={receipt.staffName} /> : null}
        <Row label="Date" value={formatDate(receipt.openedAt)} />
        <Row label="Opened" value={formatClock(receipt.openedAt)} />
        <Row label="Paid" value={receipt.paidAt ? formatClock(receipt.paidAt) : '—'} />
      </div>

      <div className="my-4 border-t border-dashed border-[#c3b79d]" />

      <div className="space-y-2">
        {receipt.lines.map((line, index) => (
          <div key={index}>
            <div className="flex justify-between gap-3">
              <span className="flex-1">
                {line.quantity}× {line.name}
              </span>
              <span className="tnum">{money(line.lineSubtotalCents)}</span>
            </div>
            {line.modifiers.length > 0 ? (
              <div className="pl-4 text-[11px] text-[#5b5348]">+ {line.modifiers.join(', ')}</div>
            ) : null}
            {line.notes ? <div className="pl-4 text-[11px] italic text-[#5b5348]">{line.notes}</div> : null}
          </div>
        ))}
      </div>

      <div className="my-4 border-t border-dashed border-[#c3b79d]" />

      <div className="space-y-1">
        <Row label="Subtotal" value={money(totals.subtotalCents)} />
        {totals.itemDiscountCents > 0 ? (
          <Row label="Item discounts" value={money(-totals.itemDiscountCents)} />
        ) : null}
        {totals.billDiscountCents > 0 ? (
          <Row
            label={
              totals.discountType === 'PERCENT'
                ? `Discount ${formatRate(totals.discountValue)}%`
                : 'Discount'
            }
            value={money(-totals.billDiscountCents)}
          />
        ) : null}
        {totals.serviceChargeCents > 0 ? (
          <Row
            label={`Service Charge ${formatRate(totals.serviceChargeBp)}%`}
            value={money(totals.serviceChargeCents)}
          />
        ) : null}
        {totals.taxBreakdown.map((bucket) => (
          <Row
            key={`${bucket.taxType}-${bucket.rateBp}`}
            label={`${bucket.taxType === 'ALCOHOL' ? 'Alcohol' : 'Food'} Tax ${formatRate(bucket.rateBp)}%`}
            value={money(bucket.taxCents)}
          />
        ))}
      </div>

      <div className="my-3 border-t-2 border-[#1a1713]" />
      <Row label="TOTAL" value={money(totals.grandTotalCents)} strong />

      {/*
        An invited table leaves no payment line, so the paper says plainly why
        nothing was collected — otherwise the copy in the file looks unpaid.
      */}
      {receipt.order.isInvite ? (
        <div className="mt-4 text-[12px]">
          <div className="text-[11px] uppercase tracking-widest text-[#5b5348]">Payment</div>
          <Row label="Invitation — with the compliments of the house" value={money(0)} />
        </div>
      ) : null}

      {receipt.payments.length > 0 ? (
        <div className="mt-4 space-y-1 text-[12px]">
          <div className="text-[11px] uppercase tracking-widest text-[#5b5348]">Payment</div>
          {receipt.payments.map((payment) => (
            <div key={payment.id}>
              <Row label={PAYMENT_METHOD_LABELS[payment.method]} value={money(payment.amountCents)} />
              {payment.changeCents ? (
                <Row label="Change given" value={money(payment.changeCents)} />
              ) : null}
              {payment.reference ? (
                <div className="text-[11px] text-[#5b5348]">Ref: {payment.reference}</div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      <div className="my-4 border-t-2 border-dashed border-[#c3b79d]" />

      <p className="text-center text-[12px] leading-relaxed">{settings.receiptThankYouMessage}</p>
      {settings.receiptFooterNote ? (
        <p className="mt-3 text-center text-[10px] leading-relaxed text-[#5b5348]">
          {settings.receiptFooterNote}
        </p>
      ) : null}
    </div>
  );
}

/** Prints whatever `.print-sheet` is on screen, using this device's printer. */
export function PrintReceiptButton({ label = 'Print here' }: { label?: string } = {}) {
  return (
    <Button className="no-print" variant="secondary" icon={<Printer size={16} />} onClick={() => window.print()}>
      {label}
    </Button>
  );
}

const PENDING_STATUSES = new Set(['QUEUED', 'PRINTING']);

/**
 * Sends the document to the till instead of printing it here.
 *
 * This is the button a waiter on a phone uses: iOS Safari has no route to a
 * printer wired to the cashier's PC, so the job goes on the queue and the print
 * station picks it up. The button then follows its own job to completion — a
 * waiter needs to know paper actually came out before they walk to the table.
 */
export function SendToPrinterButton({
  orderId,
  kind = 'RECEIPT',
  copies = 1,
  /** Print just this one split payment's slice of the bill, not the whole order. */
  paymentId,
  variant = 'primary',
  size,
  block,
}: {
  orderId: string;
  kind?: PrintJobKind;
  copies?: number;
  paymentId?: string;
  variant?: 'primary' | 'secondary';
  size?: 'sm' | 'md' | 'lg';
  block?: boolean;
}) {
  const toast = useToast();
  const [jobId, setJobId] = useState<string | null>(null);

  const queue = useMutation({
    mutationFn: () => api.orders.print(orderId, kind, copies, paymentId),
    onSuccess: (job) => setJobId(job.id),
    onError: (error: unknown) =>
      toast.error(
        error instanceof ApiError ? error.message : 'Could not reach the printer queue.',
      ),
  });

  const { data: job } = useQuery({
    queryKey: keys.printJob(jobId ?? 'none'),
    queryFn: () => api.printJobs.get(jobId!),
    enabled: jobId !== null,
    // Stop polling the moment the job reaches a state it cannot leave.
    refetchInterval: (query) =>
      PENDING_STATUSES.has(query.state.data?.status ?? 'QUEUED') ? POLL.printJob : false,
  });

  const status = job?.status;

  useEffect(() => {
    if (status === 'DONE') toast.success('Printed at the till.');
    if (status === 'FAILED') toast.error(job?.errorMessage ?? 'The till could not print this.');
    // `toast` and `job` are stable enough here; the status transition is the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const pending = queue.isPending || PENDING_STATUSES.has(status ?? '');
  const done = status === 'DONE';
  const failed = status === 'FAILED' || status === 'CANCELLED';

  const label = queue.isPending
    ? 'Sending…'
    : status === 'QUEUED'
      ? 'Waiting at the till…'
      : status === 'PRINTING'
        ? 'Printing…'
        : done
          ? 'Printed'
          : failed
            ? 'Try again'
            : 'Send to printer';

  return (
    <Button
      className="no-print"
      variant={done ? 'success' : failed ? 'danger' : variant}
      size={size}
      block={block}
      loading={pending}
      icon={done ? <Check size={16} /> : failed ? <TriangleAlert size={16} /> : <Send size={16} />}
      disabled={pending || done}
      onClick={() => {
        setJobId(null);
        queue.mutate();
      }}
    >
      {label}
    </Button>
  );
}
